import { useTranslation } from 'react-i18next';

import { getCodedAnnotationPresentation } from 'services/webediErrors/annotationPresentation';
import { WebEDIConnectorHookError } from 'services/webediErrors/types';
import {
  toggleAnnotationAcknowledgment,
  useAnnotationAcknowledgmentStore,
} from 'stores/annotationAcknowledgments/annotationAcknowledgments';
import { WithRef } from 'support/types/withRef';
import { Button } from 'components/Form/Button';

import { ActionsSection } from '../../components/ActionsSection';
import { EditDocumentButton } from '../../components/EditDocumentButton/EditDocumentButton';
import { NavigationFooter } from '../../components/NavigationFooter';
import { useIsPartnerIssueResolver } from '../../components/useIsPartnerIssueResolver';
import { FieldAnnotationPopoverShell } from '../../FieldAnnotationPopoverShell';

type Props = {
  annotation: WebEDIConnectorHookError;
  viewMode: boolean;
  style?: React.CSSProperties;
};

export const ConnectorHookError = ({ annotation, viewMode, ref, style }: WithRef<Props, HTMLDivElement>) => {
  const { t } = useTranslation();
  const isPartnerIssueResolver = useIsPartnerIssueResolver();
  const isAcknowledged = useAnnotationAcknowledgmentStore((state) => state.acknowledgedIds.has(annotation.id));

  const message = annotation.currentValue.translatedMessage;
  const hasMessage = message.trim().length > 0;

  const toggleButton =
    !viewMode && isPartnerIssueResolver ? (
      <Button
        type="button"
        variant={isAcknowledged ? 'secondary' : 'primary'}
        size="small"
        fullWidth
        aria-pressed={isAcknowledged}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => toggleAnnotationAcknowledgment(annotation.id)}
        analyticsId={isAcknowledged ? 'webedi:annotation_mark_unresolved' : 'webedi:annotation_mark_resolved'}
      >
        {isAcknowledged
          ? t('webedi:fieldAnnotation.connectorHookError.markAsUnresolved')
          : t('webedi:fieldAnnotation.connectorHookError.markAsResolved')}
      </Button>
    ) : null;

  return (
    <FieldAnnotationPopoverShell
      ref={ref}
      style={style}
      status={isAcknowledged ? 'fixed' : 'error'}
      title={t(getCodedAnnotationPresentation(annotation).titleKey)}
      description={
        hasMessage ? (
          <span data-testid="connector-hook-error-body" className="whitespace-pre-wrap">
            {message}
          </span>
        ) : undefined
      }
      actions={
        <ActionsSection>
          {toggleButton}
          <EditDocumentButton viewMode={viewMode} annotation={annotation} />
        </ActionsSection>
      }
      footer={<NavigationFooter />}
    />
  );
};

/**
 * i18n keys
 * t('webedi:fieldAnnotation.connectorHookError.title')
 */
